// NOTE: Deep clone an object to return a whole new object
function cloneObject (baseObject, newObject) {
  // Start a draft output with a shallow copy of the base object
  let output = Object.assign({}, baseObject)

  // Loop through base object to check if any property is an object or array
  // This prevents addition of keys not found in base object
  for (let key in baseObject) {
    // If new object does not have the property, clone from base object
    let property = newObject.hasOwnProperty(key) ? newObject[key] : baseObject[key]

    if (isObject(property)) {
      // If property is an object, do a recursive deep copy of the object
      output[key] = cloneObject(output[key], property)
    } else if (isArray(property)) {
      // If property is an array, create a deep copy by starting with an empty array
      output[key] = cloneArray(property)
    } else {
      // Otherwise, assign the primitive property to the key in the output
      output[key] = property
    }
  }

  return output
}

// NOTE: Deep clone an array to return a whole new array
function cloneArray (array) {
  let clonedArray = []

  // Loop through array to check if any item is an object or array
  for (let item of array) {
    if (isObject(item)) {
      // If item is an object, do a recursive deep copy of the object
      clonedArray.push(cloneObject(item, {}))
    } else if (isArray(item)) {
      // If item is an array, do a recursive deep clone of the array
      clonedArray.push(cloneArray(item))
    } else {
      // Otherwise, push the primitive item into the cloned array
      clonedArray.push(item)
    }
  }

  return clonedArray
}

function isNonEmptyObject (candidate) {
  return (
    !!candidate &&
    typeof candidate === 'object' &&
    !Array.isArray(candidate) &&
    Object.keys(candidate).length
  )
}

function isObject (candidate) {
  return (
    !!candidate &&
    typeof candidate === 'object' &&
    !Array.isArray(candidate)
  )
}

function isArray (candidate) {
  return (
    !!candidate &&
    typeof candidate === 'object' &&
    Array.isArray(candidate)
  )
}

function isInstantiatedFromClass (candidate) {
  return (
    !!candidate &&
    typeof candidate === 'object' &&
    !Array.isArray(candidate) &&
    candidate.constructor.name !== 'Object'
  )
}

function instantiateClassBasedFields (newObject, existingObject) {
  if (isNonEmptyObject(existingObject)) {
    let validated = cloneObject(newObject, existingObject)

    for (let key in validated) {
      newObject[key] = (
        !isInstantiatedFromClass(newObject[key])
          ? validated[key]
          : new (classes[newObject[key].className])(validated[key])
      )
    }
  }
}

function instantiateNonClassBasedFields (newObject, existingObject) {
  let validated = cloneObject(newObject, existingObject)

  for (let key in validated) {
    newObject[key] = validated[key]
  }
}

export class NotificationPermission {
  get className () { return 'NotificationPermission' }

  constructor (existing = {}) {
    this.status = null // "UNKNOWN", "GRANTED", "DENIED"
    this.lastRetrieved = null

    instantiateNonClassBasedFields(this, existing)
  }
}

export class CoachAssignment {
  get className () { return 'CoachAssignment' }

  constructor (existing = {}) {
    this.coachId = null
    this.assignedBy = null
    this.assignmentTime = null

    instantiateNonClassBasedFields(this, existing)
  }
}

export class PatientReminderSetting {
  get className () { return 'PatientReminderSetting' }

  constructor (existing = {}) {
    this.isAllowed = false
    this.milliseconds = null
    this.rule = null // 'beforeEvent', 'afterEvent', 'fromUTCMidnight'

    instantiateNonClassBasedFields(this, existing)
  }
}

export class PatientReminderSettings {
  get className () { return 'PatientReminderSettings' }

  constructor (existing = {}) {
    this.newAction = new classes.PatientReminderSetting({ rule: 'fromUTCMidnight' })
    this.newLesson = new classes.PatientReminderSetting({ rule: 'fromUTCMidnight' })
    this.dayBeforeAppointment = new classes.PatientReminderSetting({ rule: 'fromUTCMidnight' })
    this.dayOfAppointment = new classes.PatientReminderSetting({ rule: 'beforeEvent' })
    this.dailyActions = new classes.PatientReminderSetting({ rule: 'fromUTCMidnight' })
    this.questionnaires = new classes.PatientReminderSetting({ rule: 'fromUTCMidnight' })

    instantiateClassBasedFields(this, existing)
  }
}

export class BrazeAttributes {
  get className () { return 'BrazeAttributes' }

  constructor (existing = {}) {
    this.migraine_care_buddy_newAction_reminders_boolean = null
    this.migraine_care_buddy_newLesson_reminders_boolean = null
    this.migraine_care_buddy_dayBeforeAppointment_reminders_boolean = null
    this.migraine_care_buddy_dayOfAppointment_reminders_boolean = null
    this.migraine_care_buddy_dailyActions_reminders_boolean = null
    this.migraine_care_buddy_questionnaires_reminders_boolean = null

    instantiateNonClassBasedFields(this, existing)
  }
}

export class DraftBrazeTrackAttributeRequest {
  get className () { return 'DraftBrazeTrackAttributeRequest' }

  constructor (existing = {}) {
    this.userid = null
    this.attributes = new classes.BrazeAttributes()

    instantiateClassBasedFields(this, existing)
  }
}

export class CoachReminderSettings {
  get className () { return 'CoachReminderSettings' }

  constructor (existing = {}) {
    this.patientLang = null
    this.lastDocReminder = null
    this.lastCoachReminder = null
    this.lastMissingDocumentReminder = null
    // this.shouldSendDocReminder = false // NOTE: Deprecated in Migraine Care 2.0
    // this.shouldSendCoachReminder = false // NOTE: Deprecated in Migraine Care 2.0
    this.missingDocumentList = []

    instantiateNonClassBasedFields(this, existing)
  }
}

export class PatientData {
  get className () { return 'PatientData' }

  constructor (existing = {}) {
    // NOTE: Leyou—User sets language, coaches set PatientLang?
    this.language = null // NOTE: From URL Param if enrolled in Buddy OR from Find User By Email API endpoint if enrollment by Backoffice
    // this.patientLang = null // NOTE: DEPRECATED in Technical Upgrade

    // TODO: Renamed to `objectives` and no longer an array in Migraine Care 2.0
    this.objective = null
    // this.objectives = []

    this.startDate = null
    this.endDate = null

    // NOTE: Migraine Care 2.0
    this.study = {
      consent: null,
      timestamp: null
    }

    this.lastSeen = null // Christian: Added on 2019-12-07

    // NOTE: Deprecated in Migraine Care 2.0
    // this.studyOptInTime = null
    // this.studyOptOutTime = null

    this.coachNotes = null

    Object.defineProperties(
      this,
      {
        '_initialPreviousCoaches': {
          configurable: false,
          enumerable: false,
          writable: true,
          value: []
        },
        '_previousCoaches': {
          configurable: true,
          enumerable: false,
          writable: true,
          value: []
        },
        'previousCoaches': {
          configurable: true,
          enumerable: true,
          get () { return this._previousCoaches },
          set (newValue) {
            let hasInitialPreviousCoaches = !!this._initialPreviousCoaches.length

            if (!hasInitialPreviousCoaches) {
              this._initialPreviousCoaches = newValue.map(coach => new classes.CoachAssignment(coach))
            }

            this._previousCoaches = newValue.map(coach => new classes.CoachAssignment(coach))
          }
        },
        '_initialCurrentCoach': {
          configurable: false,
          enumerable: false,
          writable: true,
          value: new classes.CoachAssignment()
        },
        '_currentCoach': {
          configurable: false,
          enumerable: false,
          writable: true,
          value: new classes.CoachAssignment()
        },
        'currentCoach': {
          configurable: true,
          enumerable: true,
          get () { return this._currentCoach },
          set (newValue = {}) {
            let newCoach = new classes.CoachAssignment(newValue)
            let newCoachIsValid = Object.values(newCoach).every(value => !!value)

            let hasInitialCurrentCoach = !!this._initialCurrentCoach.coachId
            let hasCurrentCoach = !!this._currentCoach.coachId
            let newCoachIsDifferentFromInitialCurrentCoach = this._initialCurrentCoach.coachId !== newCoach.coachId
            let currentCoachHasChanged = this._currentCoach.coachId !== newCoach.coachId

            if (!hasInitialCurrentCoach) {
              this._initialCurrentCoach = new classes.CoachAssignment(newValue)
            }

            if (newCoachIsValid && hasCurrentCoach && currentCoachHasChanged) {
              this.previousCoaches = (
                newCoachIsDifferentFromInitialCurrentCoach
                  ? [...this._initialPreviousCoaches, this._initialCurrentCoach]
                  : [...this._initialPreviousCoaches]
              )
            }

            this._currentCoach = new classes.CoachAssignment(newValue)
          }
        }
      }
    )

    // NOTE: Deprecated in Migraine Care 2.0
    // this.coachMessage = {
    //   title: null,
    //   body: null,
    //   lastModifiedTime: null
    // }

    this.coachReminderSettings = new classes.CoachReminderSettings()
    this.patientReminderSettings = new classes.PatientReminderSettings()

    this.notificationPermission = new classes.NotificationPermission()

    // NOTE: Currently `coachReminderSettings`
    // NOTE: To be deprecated in favor of instantiation by class
    // this.patientReminderSettings = {
    //   patientLang: null,
    //   lastDocReminder: null,
    //   lastCoachReminder: null,
    //   lastMissingDocumentReminder: null,
    //   shouldSendDocReminder: false,
    //   shouldSendCoachReminder: false,
    //   missingDocumentList: []
    // }

    // NOTE: DEPRECATED in Technical Upgrade
    // NOTE: Jiechao—To deprecate because it does not seem to be used anywhere else
    // NOTE: Leyou—I have no idea what these two consents are for and taking them out doesn't seem to cause any errors
    // this.consent = {
    //   studyConsent: false, // NOTE: Result from 3E Match Study endpoint
    //   coachingConsent: false
    // }

    // NOTE: Moved to userEvents
    // this.docAppt = {
    //   timestamp: null,
    //   reschedule: false
    // }
    // this.coachAppt = {
    //   timestamp: null,
    //   reschedule: false
    // }
    // this.dailyTracking = []
    // this.oneTimeTracking = []
    // this.notTrackable = []

    instantiateClassBasedFields(this, existing)
  }
}

// Structure with only the keys necessary for PUT and POST body
export class PatientDraft {
  get className () { return 'PatientDraft' }

  constructor (existing = {}) {
    this.userId = null
    this.externalId = null
    this.name = ''
    this.employer = null
    this.data = new classes.PatientData()

    let notInvokedBySubclass = this.className === 'PatientDraft'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class Patient extends PatientDraft {
  get className () { return 'Patient' }

  constructor (existing = {}) {
    super(existing)
    this.id = null
    this.consent = null
    // this.userconsents = [] // NOTE: Commented out because we don't use it at all
    this.userPlans = []
    this.userEvents = []
    this.latestEventTimestamp = null

    instantiateClassBasedFields(this, existing)
  }
}

export class ActionData {
  get className () { return 'ActionData' }

  constructor (existing = {}) {
    this.title = null
    this.body = null
    this.shortName = null
    this.deletedStatus = false
    this.lastModifiedTime = null
    this.lastModifiedByUser = null
    this.options = new classes.ActionOptions()

    let notInvokedBySubclass = this.className === 'ActionData'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class ActionReminder {
  get className () { return 'ActionReminder' }

  constructor (existing = {}) {
    this.title = null
    this.body = null
    this.data = {
      notification: {
        buddyId: null,
        timestamps: []
      },
      isDaily: null,
      alarmDate: null,
      alarmTime: null
    }

    instantiateNonClassBasedFields(this, existing)
  }
}

export class Action {
  get className () { return 'Action' }

  constructor (existing = {}) {
    this.id = null
    this.planType = 'action'
    this.attribute = 'NVSCoaching'
    this.data = new classes.ActionData()
    this.reminders = []

    let notInvokedBySubclass = this.className === 'Action'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class UserActionData extends ActionData {
  get className () { return 'UserActionData' }

  constructor (existing = {}) {
    super(existing)
    this.id = null

    instantiateNonClassBasedFields(this, existing)
  }
}

export class UserAction extends Action {
  get className () { return 'UserAction' }

  constructor (existing = {}) {
    super(existing)

    this.data = new classes.UserActionData()

    let notInvokedBySubclass = this.className === 'UserAction'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class DraftUserAction extends UserAction {
  get className () { return 'DraftUserAction' }

  constructor (existing = {}) {
    super(existing)
    delete this.id

    instantiateClassBasedFields(this, existing)

    if (existing.constructor === Action) {
      this.data.id = existing.id
    }
  }
}

export class ActionOptions {
  get className () { return 'ActionOptions' }

  constructor (existing = {}) {
    this.startDay = null
    this.endDay = null
    this.reminder = new classes.ActionReminderOptions()
    this.belongsToTemplate = []
    this.userTracking = new classes.ActionUserTracking()

    instantiateClassBasedFields(this, existing)

    if (isNonEmptyObject(existing)) {
      this.startDay = Number.isInteger(this.startDay) ? this.startDay : Number(this.startDay)
      this.endDay = Number.isInteger(this.endDay) ? this.endDay : Number(this.endDay)
    }
  }
}

export class ActionUserTracking {
  get className () { return 'ActionUserTracking' }

  constructor (existing = {}) {
    Object.defineProperties(
      this,
      {
        '_isDaily': {
          configurable: false, // `false` to prevent Vue from adding reactivity as it should use the getter and setter
          enumerable: false, // `false` to keep it private
          writable: true,
          value: false
        },
        'isDaily': {
          configurable: true, // `true` so Vue can add reactivity
          enumerable: true, // `true` to make it public
          get () { return this._isDaily },
          set (isDaily) {
            this._isDaily = isDaily
            // NOTE: Since there is no one-time actions for Migraine Care 2.0, the behavior has been changed to disallow setting one-time trackables
            this._isDaily = this._status
            // if (!this._status) { this._isDaily = false }
          }
        },
        '_status': {
          configurable: false, // `false` to prevent Vue from adding reactivity as it should use the getter and setter
          enumerable: false, // `false` to keep it private
          writable: true,
          value: true
        },
        'status': {
          configurable: true, // `true` so Vue can add reactivity
          enumerable: true, // `true` to make it public
          get () { return this._status },
          set (status) {
            this._status = status
            // NOTE: Since there is no one-time actions for Migraine Care 2.0, the behavior has been changed to disallow setting one-time trackables
            this._isDaily = this._status
            // if (!this._status) { this._isDaily = false }
          }
        }
      }
    )

    instantiateNonClassBasedFields(this, existing)
  }
}

export class ActionReminderOptions {
  get className () { return 'ActionReminderOptions' }

  constructor (existing = {}) {
    this.title = null
    this.body = null
    this.isDaily = false
    // this.alarmTime = null // NOTE: To be confirmed: likely deprecated in Migraine Care 2.0.
    // this.alarmState = false // NOTE: To be confirmed: likely deprecated in Migraine Care 2.0.
    // this.alarmDateTime = null // NOTE: To be confirmed: likely deprecated in Migraine Care 2.0.

    instantiateNonClassBasedFields(this, existing)
  }
}

export class LessonOptions {
  get className () { return 'LessonOptions' }

  constructor (existing = {}) {
    this.startDay = null
    this.primaryIcon = null
    this.primaryLink = null
    this.primaryText = null
    this.secondaryIcon = null
    this.secondaryLink = null
    this.secondaryText = null
    this.belongsToTemplate = []

    instantiateNonClassBasedFields(this, existing)

    if (isNonEmptyObject(existing)) {
      this.startDay = Number.isInteger(this.startDay) ? this.startDay : Number(this.startDay)
    }
  }
}

export class LessonData {
  get className () { return 'LessonData' }

  constructor (existing = {}) {
    this.title = null
    this.body = null
    this.imageLink = null
    this.options = new classes.LessonOptions()
    this.shortName = null
    this.deletedStatus = false
    this.lastModifiedTime = null
    this.lastModifiedByUser = null
    this.lastSeen = null

    let notInvokedBySubclass = this.className === 'LessonData'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class Lesson {
  get className () { return 'Lesson' }

  constructor (existing = {}) {
    this.id = null
    this.planType = 'lesson'
    this.attribute = 'NVSCoaching'
    this.data = new classes.LessonData()

    let notInvokedBySubclass = this.className === 'Lesson'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class UserLessonData extends LessonData {
  get className () { return 'UserLessonData' }

  constructor (existing = {}) {
    super(existing)
    this.id = null

    instantiateNonClassBasedFields(this, existing)
  }
}

export class UserLesson extends Lesson {
  get className () { return 'UserLesson' }

  constructor (existing = {}) {
    super(existing)
    this.data = new classes.UserLessonData()

    let notInvokedBySubclass = this.className === 'UserLesson'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class DraftUserLesson extends UserLesson {
  get className () { return 'DraftUserLesson' }

  constructor (existing = {}) {
    super(existing)
    delete this.id

    instantiateClassBasedFields(this, existing)

    if (existing.constructor === Lesson) {
      this.data.id = existing.id
    }
  }
}

export class ActionTrackingData {
  get className () { return 'ActionTrackingData' }

  constructor (existing = {}) {
    this.actionId = null
    this.trackingType = null
    this.completionStatus = null

    instantiateNonClassBasedFields(this, existing)
  }
}

export class ActionTracking {
  get className () { return 'ActionTracking' }

  constructor (existing = {}) {
    this.type = 'action'
    this.startTimestamp = null
    this.endTimestamp = null
    this.data = new classes.ActionTrackingData()

    instantiateClassBasedFields(this, existing)
  }
}

export class AppointmentData {
  get className () { return 'AppointmentData' }

  constructor (existing = {}) {
    this.appointmentType = null
    this.rescheduledAppointmentId = null
    this.isRescheduled = false
    this.toReschedule = false
    this.isCanceled = false
    this.hasReminder = false
    this.reminders = []

    instantiateNonClassBasedFields(this, existing)
  }
}

export class UserEventReminderProperties {
  get className () { return 'UserEventReminderProperties' }

  constructor (existing = {}) {
    this.language = null
    this.url = null
    instantiateNonClassBasedFields(this, existing)
  }
}

export class UserEventReminder {
  get className () { return 'UserEventReminder' }

  constructor (existing = {}) {
    this.eventName = null
    this.properties = new classes.UserEventReminderProperties()
    this.timestamps = []
    instantiateClassBasedFields(this, existing)
  }
}

export class UserEventData {
  get className () { return 'UserEventData' }

  constructor (existing = {}) {
    this.reminders = new classes.UserEventReminder()

    instantiateClassBasedFields(this, existing)
  }
}

export class UserEvent {
  get className () { return 'UserEvent' }

  constructor (existing = {}) {
    this.type = null
    this.startTimestamp = null
    this.endTimestamp = null
    this.data = new classes.UserEventData()

    instantiateClassBasedFields(this, existing)
  }
}

export class QuestionnaireEventData {
  get className () { return 'QuestionnaireEventData' }

  constructor (existing = {}) {
    this.reminders = []
    // this.completed = false
    this.expirationTimestamp = null
    this.recurringOrder = null

    instantiateClassBasedFields(this, existing)
  }
}

export class DraftQuestionnaireEvent {
  get className () { return 'DraftQuestionnaireEvent' }

  constructor (existing = {}) {
    this.type = 'questionnaire'
    this.startTimestamp = null
    this.endTimestamp = null
    this.data = new classes.QuestionnaireEventData()

    let notInvokedBySubclass = this.className === 'DraftQuestionnaireEvent'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class QuestionnaireEvent extends DraftQuestionnaireEvent {
  get className () { return 'QuestionnaireEvent' }

  constructor (existing = {}) {
    super(existing)
    this.id = null

    instantiateClassBasedFields(this, existing)
  }
}

export class ExitQuestionnaireEvent {
  get className () { return 'UserEvent' }

  constructor (existing = {}) {
    this.type = 'exitQuestionnaire'
    this.startTimestamp = null
    this.endTimestamp = null
    this.data = new classes.UserEventData()

    instantiateClassBasedFields(this, existing)
  }
}

export class DraftAppointment {
  get className () { return 'DraftAppointment' }

  constructor (existing = {}) {
    this.type = 'appointment'
    this.startTimestamp = null
    this.endTimestamp = null
    this.data = new classes.AppointmentData()

    let notInvokedBySubclass = this.className === 'DraftAppointment'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class Appointment extends DraftAppointment {
  get className () { return 'Appointment' }

  constructor (existing = {}) {
    super(existing)
    this.id = null

    instantiateClassBasedFields(this, existing)
  }
}

export class ProgramPlanData {
  get className () { return 'ProgramPlanData' }

  constructor (existing = {}) {
    this.title = null
    this.deletedStatus = false
    this.childPlans = []

    instantiateNonClassBasedFields(this, existing)
  }
}

export class ProgramPlan {
  get className () { return 'ProgramPlan' }

  constructor (existing = {}) {
    this.id = null
    this.planType = null
    this.attribute = 'NVSCoaching'
    this.programId = null
    this.data = new classes.ProgramPlanData()

    let notInvokedBySubclass = this.className === 'ProgramPlan'

    if (notInvokedBySubclass) {
      instantiateClassBasedFields(this, existing)
    }
  }
}

export class DraftProgramPlan extends ProgramPlan {
  get className () { return 'DraftProgramPlan' }

  constructor (existing = {}) {
    super(existing)
    delete this.id

    instantiateClassBasedFields(this, existing)
  }
}

export const classes = {
  NotificationPermission,
  CoachAssignment,
  PatientReminderSetting,
  PatientReminderSettings,
  BrazeAttributes,
  DraftBrazeTrackAttributeRequest,
  CoachReminderSettings,
  PatientData,
  PatientDraft,
  Patient,
  ActionData,
  ActionReminder,
  Action,
  UserActionData,
  UserAction,
  DraftUserAction,
  ActionOptions,
  ActionUserTracking,
  ActionReminderOptions,
  LessonOptions,
  LessonData,
  Lesson,
  UserLessonData,
  UserLesson,
  DraftUserLesson,
  ActionTrackingData,
  ActionTracking,
  AppointmentData,
  DraftAppointment,
  Appointment,
  ProgramPlanData,
  ProgramPlan,
  DraftProgramPlan,
  UserEventReminderProperties,
  UserEventReminder,
  UserEventData,
  UserEvent,
  ExitQuestionnaireEvent,
  DraftQuestionnaireEvent,
  QuestionnaireEventData,
  QuestionnaireEvent
}
